<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/feedback">Feedback</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">
        {{ id() }}
      </span>
    </nav>
  </header>

  @if (loading()) {
    <section class="sg-panel fbd-panel" aria-label="Loading report">
      <span class="sg-skeleton sg-skeleton--text sg-skeleton--w40"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 90%"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 75%"></span>
    </section>
  } @else if (error() || !report()) {
    <section class="sg-empty sg-empty--bordered" aria-label="Report unavailable">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this report</p>
      <p class="sg-empty__desc">
        It may have been removed, or something went wrong fetching it.
      </p>
      <button mat-stroked-button (click)="retry()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </section>
  } @else if (report(); as r) {
    <div class="page-head__bar fbd-head">
      <div class="page-head__titles">
        <h1 class="page-head__title">
          {{ r.tag === 'ASSESSMENT' ? 'Assessment' : r.kind_display }} report
          <span class="fbd-ref">{{ r.short_id }}</span>
        </h1>
        <p class="page-head__subtitle">
          Reported by {{ r.reporter_name | dash }} on {{ r.created_at | date: 'medium' }}
        </p>
      </div>
      <app-status-chip [status]="r.triage_status" />
    </div>

    <div class="fbd-grid">
      <!-- ─── Left: what they said, and what we're doing about it ────────── -->
      <div class="fbd-col">
        <section class="sg-panel fbd-panel" aria-label="What they reported">
          <h2 class="fbd-panel__title">What they reported</h2>
          <p class="fbd-message">{{ r.message }}</p>

          <!-- FLOW-347: what *Flag this* carried, exactly as it arrived. -->
          @if (r.tag) {
            <h3 class="fbd-panel__title">What the form carried</h3>
            <pre class="fbd-carried" data-testid="fbd-carried">{{ r.context | json }}</pre>
          }

          @if (r.screenshot_id) {
            <div class="fbd-shot">
              @if (screenshotUrl(); as url) {
                <a [href]="url" target="_blank" rel="noopener">
                  <img [src]="url" alt="The screenshot attached to this report" />
                </a>
                <p class="fbd-hint">Opens full size in a new tab. The link expires shortly.</p>
              } @else {
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="loadingScreenshot()"
                  (click)="viewScreenshot()"
                >
                  @if (loadingScreenshot()) {
                    <mat-spinner diameter="18" /> Loading…
                  } @else {
                    <app-icon name="image" [size]="16" /> View screenshot
                  }
                </button>
              }
            </div>
          }
        </section>

        <!-- Setting a status APPENDS to the trail; it never edits a past one. -->
        <section class="sg-panel fbd-panel" aria-label="Set status">
          <h2 class="fbd-panel__title">Set status</h2>
          <form [formGroup]="form" class="fbd-form">
            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select formControlName="to_status">
                @for (c of choices; track c.value) {
                  <mat-option [value]="c.value">{{ c.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Note (optional)</mat-label>
              <textarea
                matInput
                formControlName="note"
                rows="3"
                placeholder="Why this status — the next person reading this will thank you."
              ></textarea>
            </mat-form-field>

            @if (saveError(); as message) {
              <div class="error-banner" role="alert">{{ message }}</div>
            }

            <div class="fbd-form__actions">
              <button mat-flat-button type="button" [disabled]="saving()" (click)="save()">
                @if (saving()) {
                  <mat-spinner diameter="20" />
                } @else {
                  Save status
                }
              </button>
            </div>
          </form>
        </section>

        <section class="sg-panel fbd-panel" aria-label="Status history">
          <h2 class="fbd-panel__title">Status history</h2>
          @if (!events().length) {
            <p class="fbd-hint">Nothing recorded yet — this report is still new.</p>
          } @else {
            <ol class="fbd-timeline">
              @for (e of events(); track e.id) {
                <li class="fbd-timeline__item">
                  <div class="fbd-timeline__head">
                    <app-status-chip [status]="e.to_status" />
                    <span class="fbd-muted">
                      {{ e.actor_name | dash }} · {{ e.created_at | date: 'medium' }}
                    </span>
                  </div>
                  @if (e.note) {
                    <p class="fbd-timeline__note">{{ e.note }}</p>
                  }
                </li>
              }
            </ol>
          }
        </section>
      </div>

      <!-- ─── Right: the context the report was submitted with ───────────── -->
      <aside class="fbd-col">
        <section class="sg-panel fbd-panel" aria-label="Where it happened">
          <h2 class="fbd-panel__title">Where it happened</h2>
          <dl class="fbd-facts">
            <dt>Page</dt>
            <dd class="fbd-break">{{ r.url | dash }}</dd>

            <dt>Reported by</dt>
            <dd>
              {{ r.reporter_name | dash }}
              @if (r.reporter_email) {
                <br />
                <a [href]="'mailto:' + r.reporter_email">{{ r.reporter_email }}</a>
              }
            </dd>

            <dt>Browser window</dt>
            <dd>
              @if (r.viewport_width) {
                {{ r.viewport_width }} × {{ r.viewport_height }}
              } @else {
                —
              }
            </dd>

            <dt>Screen</dt>
            <dd>
              @if (r.screen_width) {
                {{ r.screen_width }} × {{ r.screen_height }}
                @if (r.device_pixel_ratio) {
                  (at {{ r.device_pixel_ratio }}×)
                }
              } @else {
                —
              }
            </dd>

            <dt>Device</dt>
            <dd>{{ r.platform | dash }}</dd>

            <dt>Timezone</dt>
            <dd>{{ r.timezone | dash }}</dd>

            <dt>Language</dt>
            <dd>{{ r.language | dash }}</dd>

            <dt>Browser</dt>
            <dd class="fbd-break fbd-ua">{{ r.user_agent | dash }}</dd>
          </dl>
        </section>

        <section class="sg-panel fbd-panel" aria-label="Notification">
          <h2 class="fbd-panel__title">Notification</h2>
          <dl class="fbd-facts">
            <dt>Email</dt>
            <dd><app-status-chip [status]="r.status" /></dd>
            @if (r.error) {
              <dt>Error</dt>
              <dd class="fbd-break fbd-ua">{{ r.error }}</dd>
            }
            <dt>Last triaged</dt>
            <dd>
              @if (r.triaged_at) {
                {{ r.triaged_by_name | dash }}<br />
                {{ r.triaged_at | date: 'medium' }}
              } @else {
                —
              }
            </dd>
          </dl>
        </section>
      </aside>
    </div>
  }
</div>
